import React from 'react';
import { Header, Icon, Segment, Button, Form, Grid, Message } from 'semantic-ui-react';
import type { ContractTestemunhaFormItem } from '../../types/contractForm';
import { formatCpfProgressive, cleanCpf } from '../../services/utils/stringUtils';

export interface ContractFormTestemunhasTabProps {
  testemunhas: ContractTestemunhaFormItem[];
  setTestemunhas: React.Dispatch<React.SetStateAction<ContractTestemunhaFormItem[]>>;
  testemunhaAtual: { nome: string; cpf: string; rg: string };
  setTestemunhaAtual: React.Dispatch<React.SetStateAction<{ nome: string; cpf: string; rg: string }>>;
}

export const ContractFormTestemunhasTab: React.FC<ContractFormTestemunhasTabProps> = ({
  testemunhas,
  setTestemunhas,
  testemunhaAtual,
  setTestemunhaAtual,
}) => {
  return (
    <div style={{ padding: '20px' }}>
      <Header as="h3" style={{ color: '#2c3e50', marginBottom: '20px' }}>
        <Icon name="eye" />
        Testemunhas (Opcional)
      </Header>

      <p style={{ color: '#666', marginBottom: '20px' }}>
        Adicione até 2 testemunhas para o contrato (opcional, mas recomendado para maior validade jurídica).
      </p>

      {testemunhas.length > 0 && (
        <Segment style={{ marginBottom: '20px' }}>
          <Header as="h4" style={{ color: '#2c3e50' }}>
            <Icon name="eye" />
            Testemunhas Cadastradas ({testemunhas.length}/2)
          </Header>
          {testemunhas.map((test, index) => (
            <div
              key={test.id}
              style={{
                padding: '10px',
                borderBottom: index < testemunhas.length - 1 ? '1px solid #eee' : 'none',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
              }}
            >
              <div>
                <strong>{test.nome}</strong>
                <br />
                <small style={{ color: '#666' }}>
                  CPF: {test.cpf} | RG: {test.rg}
                </small>
              </div>
              <Button
                size="small"
                color="red"
                icon="trash"
                onClick={() => {
                  setTestemunhas((prev) => prev.filter((t) => t.id !== test.id));
                }}
              />
            </div>
          ))}
        </Segment>
      )}

      {testemunhas.length < 2 && (
        <Segment>
          <Header as="h4" style={{ color: '#2c3e50', marginBottom: '15px' }}>
            <Icon name="plus" />
            Adicionar Testemunha
          </Header>

          <Grid stackable>
            <Grid.Row>
              <Grid.Column width={8}>
                <Form.Field required>
                  <label>Nome Completo</label>
                  <input
                    type="text"
                    placeholder="Digite o nome completo da testemunha"
                    value={testemunhaAtual.nome}
                    onChange={(e) => setTestemunhaAtual((prev) => ({ ...prev, nome: e.target.value }))}
                    style={{
                      width: '100%',
                      padding: '10px',
                      border: '1px solid #ccc',
                      borderRadius: '4px',
                      fontSize: '14px',
                    }}
                  />
                </Form.Field>
              </Grid.Column>

              <Grid.Column width={4}>
                <Form.Field required>
                  <label>CPF</label>
                  <input
                    type="text"
                    placeholder="000.000.000-00"
                    value={formatCpfProgressive(testemunhaAtual.cpf)}
                    onChange={(e) => {
                      const cleanedValue = cleanCpf(e.target.value);
                      setTestemunhaAtual((prev) => ({ ...prev, cpf: cleanedValue }));
                    }}
                    maxLength={14}
                    style={{
                      width: '100%',
                      padding: '10px',
                      border: '1px solid #ccc',
                      borderRadius: '4px',
                      fontSize: '14px',
                    }}
                  />
                  <div style={{ fontSize: '11px', color: '#666', marginTop: '2px' }}>Máximo 11 dígitos</div>
                </Form.Field>
              </Grid.Column>

              <Grid.Column width={4}>
                <Form.Field required>
                  <label>RG</label>
                  <input
                    type="text"
                    placeholder="0000000000"
                    value={testemunhaAtual.rg}
                    onChange={(e) => {
                      const value = e.target.value.replace(/\D/g, '').substring(0, 10);
                      setTestemunhaAtual((prev) => ({ ...prev, rg: value }));
                    }}
                    maxLength={10}
                    style={{
                      width: '100%',
                      padding: '10px',
                      border: '1px solid #ccc',
                      borderRadius: '4px',
                      fontSize: '14px',
                    }}
                  />
                  <div style={{ fontSize: '11px', color: '#666', marginTop: '2px' }}>Máximo 10 dígitos</div>
                </Form.Field>
              </Grid.Column>
            </Grid.Row>

            <Grid.Row>
              <Grid.Column width={16}>
                <Button
                  color="green"
                  onClick={() => {
                    if (testemunhaAtual.nome.trim() && testemunhaAtual.cpf.trim() && testemunhaAtual.rg.trim()) {
                      const novaTestemunha: ContractTestemunhaFormItem = {
                        id: `testemunha-${Date.now()}`,
                        nome: testemunhaAtual.nome.trim(),
                        cpf: testemunhaAtual.cpf.trim(),
                        rg: testemunhaAtual.rg.trim(),
                      };
                      setTestemunhas((prev) => [...prev, novaTestemunha]);
                      setTestemunhaAtual({ nome: '', cpf: '', rg: '' });
                    }
                  }}
                  disabled={
                    !testemunhaAtual.nome.trim() || !testemunhaAtual.cpf.trim() || !testemunhaAtual.rg.trim()
                  }
                >
                  <Icon name="plus" />
                  Adicionar Testemunha
                </Button>
              </Grid.Column>
            </Grid.Row>
          </Grid>
        </Segment>
      )}

      {testemunhas.length === 2 && (
        <Message success>
          <Icon name="check circle" />
          Você já cadastrou 2 testemunhas. O número máximo foi atingido.
        </Message>
      )}

      {testemunhas.length === 0 && (
        <Message info>
          <Icon name="info circle" />
          Testemunhas são opcionais, mas recomendadas para maior validade jurídica do contrato.
        </Message>
      )}

      <Message info style={{ marginTop: '20px' }}>
        <Message.Header>Dicas sobre Testemunhas</Message.Header>
        <p>
          As testemunhas devem ser maiores de 18 anos e não podem ter interesse direto no contrato. É recomendado
          incluir pelo menos uma testemunha para maior segurança jurídica.
        </p>
      </Message>
    </div>
  );
};
